import { useState, useEffect, useRef } from "react";
import { Edit2, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { getSubscriptionPlans } from "@/services/masters";
import { getSubscriptionsByStateCity } from "@/services/branchIncharge";
import { getZoneSubscriptions } from "@/services/zoneManager";
import SubscriptionModal from "./SubscriptionModal";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import { useRoleContext } from "@/hooks/useRoleContext";
import Pagination from "../common/Pagination";

interface SubscriptionRange {
  Period: string;
  KM_Range: string;
  Base_Price: string;
  Final_Amount: string;
  Discount_Amount: string;
  Discount_Percentage: number;
}

interface Subscription {
  id: number;
  Plan_Name: string;
  State: string;
  City: string;
  Base_Price: number;
  Base_Distance: number;
  Base_Distance_Units: string;
  Distance_Step: number;
  Distance_Step_Units: string;
  Rate_Per_Meter: number;
  Ranges: SubscriptionRange[];
}
interface SubscriptionsTabProps {
  searchQuery: string;
  onAddHandlerReady: (handler: () => void) => void;
  stateCodeOverride?: string;
  cityCodeOverride?: string;
}

export default function SubscriptionsTab({ searchQuery, onAddHandlerReady, stateCodeOverride, cityCodeOverride }: SubscriptionsTabProps) {
  const { canUpdate } = useScreenPermission("plans");
  const { isZoneManager, stateCode: ctxState, cityCode: ctxCity, zoneCode,role } = useRoleContext();
  const stateCode = stateCodeOverride || ctxState;
  const cityCode = role == 'parent' ? '' : cityCodeOverride || ctxCity;
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [planFilter, setPlanFilter] = useState<string>("All Plans");
  const [selectedKmRanges, setSelectedKmRanges] = useState<Record<number, string>>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const isInitialMount = useRef(true);

  const loadSubscriptions = async (plan?: string, p: number = 1, search: string = "") => {
    try {
      setLoading(true);
      const res = isZoneManager && zoneCode
        ? await getZoneSubscriptions(zoneCode, p)
        : role !== "admin" && stateCode
          ? await getSubscriptionsByStateCity(stateCode, cityCode, plan, p, search)
          : await getSubscriptionPlans(plan, p, undefined, undefined, search || undefined);
      const { data } = res;
      const d = data as any;
      const items: Subscription[] = d?.results ?? (Array.isArray(d) ? d : []);
      setSubscriptions(items);
      setTotalPages(d?.total_pages ?? 1);
      setTotalCount(d?.count ?? items.length);
    } catch (error) {
      toast.error("Error loading subscriptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      loadSubscriptions(undefined, 1);
      return;
    }
    const id = setTimeout(() => {
      setPage(1);
      loadSubscriptions(planFilter === "All Plans" ? undefined : planFilter, 1, searchQuery);
    }, 500);
    return () => clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    onAddHandlerReady(() => handleAdd);
  }, [onAddHandlerReady]);

  const handlePlanFilter = (plan: string) => {
    setPlanFilter(plan);
    setPage(1);
    loadSubscriptions(plan === "All Plans" ? undefined : plan, 1, searchQuery);
  };

  const handleAdd = () => {
    setEditingSubscription(null);
    setIsModalOpen(true);
  };

  const handleEdit = (subscription: any) => {
    setEditingSubscription(subscription);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-brand-500 border-t-transparent"></div>
        <p className="text-gray-600 mt-4">Loading subscriptions...</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Filter by Plan */}
        
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={totalCount}
        itemsPerPage={20}
        onPageChange={(p) => { setPage(p); loadSubscriptions(planFilter === "All Plans" ? undefined : planFilter, p, searchQuery); }}
      />
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Filter by Plan:</h3>
          <div className="flex gap-3">
            {["All Plans", "Basic", "Premium"].map((plan) => (
              <button
                key={plan}
                onClick={() => handlePlanFilter(plan)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${planFilter === plan
                  ? "bg-brand-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                {plan}
              </button>
            ))}
          </div>
        </div>

        {/* Subscription Cards */}
        {subscriptions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-500">No subscriptions found</p>
          </div>
        ) : (
          subscriptions.map((subscription) => {
            // Get unique KM ranges from the subscription
            const kmRanges = [...new Set(subscription.Ranges?.map((r: any) => r.KM_Range) || [])];

            // Get selected KM range for this subscription (default to first available)
            const subscriptionKmRange = selectedKmRanges[subscription.id] || kmRanges[0] || "0Km-5KM";
            const currentKmRangeData = subscription.Ranges?.filter((r: any) => r.KM_Range === subscriptionKmRange) || [];
            return (
              <div key={subscription.id} className="bg-brand-50 rounded-xl shadow-sm border border-brand-200 p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{subscription.Plan_Name}</h3>
                    <div className="flex flex-row flex-wrap justify-center items-center gap-6 mt-2">
                      <p>Base Price: ₹{subscription.Base_Price}</p>

                      <p>
                        Distance:
                        {subscription.Base_Distance}
                        {subscription.Base_Distance_Units}
                      </p>

                      <p>
                        Step:
                        {subscription.Distance_Step}
                        {subscription.Distance_Step_Units}
                      </p>

                      <p>
                        Rate/Meter:
                        ₹{subscription.Rate_Per_Meter}
                      </p>
                    </div>
                    <p className="text-gray-600 mt-1">
                      {subscription.State ?? "-"}, {subscription.City ?? "-"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {canUpdate && (
                      <button
                        onClick={() => handleEdit(subscription)}
                        className="p-2 text-brand-500 hover:bg-brand-100 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* KM Range Selector */}
                {kmRanges.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Select KM Range:</p>
                    <div className="flex gap-2 flex-wrap">
                      {kmRanges.map((range: string) => (
                        <button
                          key={range}
                          onClick={() => setSelectedKmRanges(prev => ({ ...prev, [subscription.id]: range }))}
                          className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${subscriptionKmRange === range
                            ? "bg-brand-500 text-white"
                            : "bg-white text-brand-700 border border-brand-300 hover:bg-brand-50"
                            }`}
                        >
                          {range.replace("Km", " KM")}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Duration Pricing */}
                {currentKmRangeData.length > 0 && (
                  <div>
                    <div className="bg-brand-100 p-4 rounded-lg border border-brand-300 mb-4">
                      <div className="text-sm font-semibold text-gray-700">
                        <span className="text-brand-700">
                          Base Distance:
                          {subscription.Base_Distance}
                          {subscription.Base_Distance_Units}
                        </span>{" "}
                        <p>
                          Rate Per Meter:
                          ₹{subscription.Rate_Per_Meter}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {currentKmRangeData.map((range: any, index: number) => (
                        <div key={index} className="bg-white p-5 rounded-xl border-2 border-gray-300 hover:border-brand-400 hover:shadow-lg transition-all">
                          <div className="mb-3">
                            <h4 className="font-bold text-gray-900 text-lg">{range.Period}</h4>
                            <p className="text-sm text-gray-600">
                              {range.Period === "1 Month"
                                ? "30 days"
                                : range.Period === "3 Months"
                                  ? "90 days"
                                  : range.Period === "6 Months"
                                    ? "180 days"
                                    : "330 days"}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Base:</span>
                              <span className="font-semibold text-gray-900">₹{range.Base_Price}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-orange-600">Discount:</span>
                              <span className="font-semibold text-orange-600">-{range.Discount_Percentage}%</span>
                            </div>
                            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                              <span className="text-sm font-bold text-gray-900">Final:</span>
                              <span className="font-bold text-green-600 text-xl">₹{range.Final_Amount}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        subscription={editingSubscription}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingSubscription(null);
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          setEditingSubscription(null);
          loadSubscriptions(planFilter === "All Plans" ? undefined : planFilter, page, searchQuery);
        }}
      />

    </>
  );
}
